import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { switchMap } from 'rxjs/operators';
import { Account } from 'projects/internal/r1-component-library/src/lib/components/appcloud-login/appcloud-credentials/appcloud-credentials.model';
import { AccountPatch, ManageAccountsService } from '../services/manage-accounts.service';
import { withPagedSearch } from './with-paged-search.feature';
import { PagedSearch } from '../models/paged-search.model';
import { ChartSeries, TIMESPAN_OPTIONS, TimespanOption } from '../models/account-creation-point.model';

export type AccountsState = {
    items: Account[];
    selectedId: number | null;
    editing: boolean;
    kpiOption: TimespanOption;
    kpiCount: number | null;
    chartDays: number;
    chartSeries: ChartSeries[];
};

const initialState: AccountsState = {
    items: [],
    selectedId: null,
    editing: false,
    kpiOption: TIMESPAN_OPTIONS[0],
    kpiCount: null,
    chartDays: 30,
    chartSeries: [],
};

/**
 * "Accounts" tool page: serverside paged table, KPI card, creation chart and the
 * view/edit side panel. Provided by `AccountsListComponent`. Replaces the former NGXS
 * `AccountManagerState` + CDK `AccountsDataSource`.
 */
export const AccountsStore = signalStore(
    withState(initialState),
    withPagedSearch({ sortField: 'name', sortOrder: 'asc' }),
    withProps(() => ({
        _accountsService: inject(ManageAccountsService),
    })),
    withComputed(store => ({
        selectedAccount: computed(() => store.items().find(account => account.id === store.selectedId()) ?? null),
        sidenavOpen: computed(() => store.selectedId() !== null),
    })),
    withMethods(store => {
        const load = rxMethod<PagedSearch>(
            switchMap(search => {
                store.setLoading(true);
                return store._accountsService.findAccounts(search).pipe(
                    tapResponse({
                        next: page => {
                            patchState(store, { items: page?.content ?? [] });
                            store.setTotalElements(page?.totalElements ?? 0);
                        },
                        error: () => patchState(store, { items: [] }),
                        finalize: () => store.setLoading(false),
                    })
                );
            })
        );

        const loadKpi = rxMethod<TimespanOption>(
            switchMap(option =>
                store._accountsService.countAccounts(option.days).pipe(
                    tapResponse({
                        next: count => patchState(store, { kpiCount: count }),
                        error: () => patchState(store, { kpiCount: null }),
                    })
                )
            )
        );

        const loadChart = rxMethod<number>(
            switchMap(days =>
                store._accountsService.accountCreationSeries(days).pipe(
                    tapResponse({
                        next: series => patchState(store, { chartSeries: series }),
                        error: () => patchState(store, { chartSeries: [] }),
                    })
                )
            )
        );

        return {
            load,
            loadKpi,
            loadChart,
            reload() {
                load(store.search());
            },
            setKpiOption(option: TimespanOption) {
                patchState(store, { kpiOption: option });
            },
            setChartDays(days: number) {
                patchState(store, { chartDays: days });
            },
            view(account: Account) {
                patchState(store, { selectedId: account.id ?? null, editing: false });
            },
            edit(account: Account) {
                patchState(store, { selectedId: account.id ?? null, editing: true });
            },
            toggleEditing() {
                patchState(store, { editing: !store.editing() });
            },
            closeSidenav() {
                patchState(store, { selectedId: null, editing: false });
            },
            patchAccount: rxMethod<{ id: number; patch: AccountPatch }>(
                switchMap(({ id, patch }) =>
                    store._accountsService.patchAccount(id, patch).pipe(
                        tapResponse({
                            next: updated => {
                                patchState(store, {
                                    items: store
                                        .items()
                                        .map(account =>
                                            account.id === id ? { ...account, ...patch, ...updated } : account
                                        ),
                                    editing: false,
                                });
                            },
                            error: () => undefined,
                        })
                    )
                )
            ),
            deleteAccount: rxMethod<number>(
                switchMap(id =>
                    store._accountsService.deleteAccount(id).pipe(
                        tapResponse({
                            next: () => {
                                if (store.selectedId() === id) {
                                    patchState(store, { selectedId: null, editing: false });
                                }
                                load(store.search());
                                loadKpi(store.kpiOption());
                            },
                            error: () => undefined,
                        })
                    )
                )
            ),
        };
    }),
    withHooks({
        onInit(store) {
            // rxMethods accept signals: every change to search/kpiOption/chartDays re-queries.
            store.load(store.search);
            store.loadKpi(store.kpiOption);
            store.loadChart(store.chartDays);
        },
    })
);
